import { apiClient } from "@/lib/apiClient";
import {
  CreateCustomerDto,
  UpdateCustomerDto,
  CreateCustomerAddressDto,
  CreateCustomerNoteDto,
  FetchCustomersOptions,
} from "@/types/models/customer";

// Customer filtering and pagination options
export interface SearchCustomersOptions {
  q: string;
  page?: number;
  page_size?: number;
}

// Customer CRUD Operations
export const getCustomers = async (options: FetchCustomersOptions = {}) => {
  const params: Record<string, string> = {};

  if (options.page) params.page = options.page.toString();
  if (options.limit) params.page_size = options.limit.toString();
  if (options.sort_by) params.sort_by = options.sort_by;
  if (options.sort_order) params.sort_order = options.sort_order;

  return await apiClient.getPaginated(
    "/api/v1/admin/:storeId/customer/list",
    params,
  );
};

export const getCustomerById = async (customerId: string) => {
  return await apiClient.get(`/api/v1/admin/:storeId/customer/${customerId}`);
};

export const searchCustomers = async (options: SearchCustomersOptions) => {
  const params: Record<string, string> = {
    q: options.q,
  };

  if (options.page) params.page = options.page.toString();
  if (options.page_size) params.page_size = options.page_size.toString();

  return await apiClient.getPaginated(
    "/api/v1/admin/:storeId/customer/search",
    params,
  );
};

export const createCustomer = async (customer: CreateCustomerDto) => {
  return await apiClient.post(
    "/api/v1/admin/:storeId/customer/add-customer",
    customer,
  );
};

export const updateCustomer = async (
  customerId: string,
  customer: UpdateCustomerDto,
) => {
  return await apiClient.put(
    `/api/v1/admin/:storeId/customer/${customerId}`,
    customer,
  );
};

export const deleteCustomer = async (customerId: string) => {
  return await apiClient.delete(
    `/api/v1/admin/:storeId/customer/${customerId}`,
  );
};

// Customer Address Operations
export const getCustomerAddresses = async (
  customerId: string,
  page = 1,
  pageSize = 10,
) => {
  const params = {
    page: page.toString(),
    page_size: pageSize.toString(),
  };

  return await apiClient.getPaginated(
    `/api/v1/admin/:storeId/customer/${customerId}/address`,
    params,
  );
};

export const createCustomerAddress = async (
  customerId: string,
  address: CreateCustomerAddressDto,
) => {
  return await apiClient.post(
    `/api/v1/admin/:storeId/customer/${customerId}/address`,
    address,
  );
};

export const deleteCustomerAddress = async (
  customerId: string,
  addressId: string,
) => {
  return await apiClient.delete(
    `/api/v1/admin/:storeId/customer/${customerId}/address/${addressId}`,
  );
};

// Customer Notes Operations
export const getCustomerNotes = async (
  customerId: string,
  page = 1,
  pageSize = 10,
) => {
  const params = {
    page: page.toString(),
    page_size: pageSize.toString(),
  };

  return await apiClient.getPaginated(
    `/api/v1/admin/:storeId/customer/${customerId}/note`,
    params,
  );
};

export const createCustomerNote = async (
  customerId: string,
  note: CreateCustomerNoteDto,
) => {
  return await apiClient.post(
    `/api/v1/admin/:storeId/customer/${customerId}/note`,
    note,
  );
};

export const deleteCustomerNote = async (
  customerId: string,
  noteId: string,
) => {
  return await apiClient.delete(
    `/api/v1/admin/:storeId/customer/${customerId}/note/${noteId}`,
  );
};

// Dashboard and Analytics
export const getCustomerDashboard = async () => {
  return await apiClient.get("/api/v1/admin/:storeId/customer/dashboard");
};

// Import/Export Operations
export const exportCustomers = async () => {
  return await apiClient.get("/api/v1/admin/:storeId/customer/export");
};

export const importCustomers = async (file: File) => {
  return await apiClient.uploadFile(
    "/api/v1/admin/:storeId/customer/import",
    file,
  );
};

// Bulk Operations
export const bulkDeleteCustomers = async (customerIds: string[]) => {
  return await apiClient.delete("/api/v1/admin/:storeId/customer/bulk", {
    customerIds,
  });
};

export const bulkUpdateCustomers = async (
  customerIds: string[],
  updates: Partial<UpdateCustomerDto>,
) => {
  return await apiClient.put("/api/v1/admin/:storeId/customer/bulk", {
    customerIds,
    updates,
  });
};
